import { Link } from "react-router-dom";
import { useState, useEffect } from "react";

export default function Landing() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const stats = [
    { value: "104", label: "Matches to predict" },
    { value: "12", label: "Groups" },
    { value: "48", label: "Nations" },
    { value: "37", label: "Days of football" },
  ];

  const howItWorks = [
    {
      number: "01",
      title: "SIGN UP FREE",
      description: "Create your account in seconds. No credit card, no catch — completely free to play.",
    },
    {
      number: "02",
      title: "MAKE PREDICTIONS",
      description: "Pick the score and first goalscorer for every World Cup match before kick-off.",
    },
    {
      number: "03",
      title: "EARN POINTS",
      description: "5 points for an exact score, 2 for the correct winner, 3 bonus points for the first goalscorer.",
    },
    {
      number: "04",
      title: "COMPETE",
      description: "Create a private league, share your invite code, and see who really knows their football.",
    },
  ];

  const points = [
    {
      label: "Exact Score",
      pts: 5,
      colour: "#C8FF00",
      example: "You predicted 2-1, it finished 2-1",
    },
    {
      label: "Correct Winner",
      pts: 2,
      colour: "#3B82F6",
      example: "You predicted the right team to win",
    },
    {
      label: "First Goalscorer",
      pts: 3,
      colour: "#FF5C00",
      example: "Your player scored first",
    },
  ];

  const mockLeaderboard = [
    { name: "TopBanana99", pts: 47, scores: 4, winners: 6, scorers: 3, medal: "🥇" },
    { name: "PredictorKing", pts: 38, scores: 3, winners: 5, scorers: 2, medal: "🥈" },
    { name: "FootyOracle", pts: 31, scores: 2, winners: 5, scorers: 1, medal: "🥉" },
    { name: "GoalMachine", pts: 24, scores: 1, winners: 4, scorers: 2, medal: "4" },
    { name: "MatchdayMike", pts: 19, scores: 1, winners: 3, scorers: 1, medal: "5" },
  ];

  const features = [
    "Create unlimited private leagues — completely free",
    "Share via a unique 6-character invite code",
    "Live leaderboard updates after every match",
    "Track exact scores, correct winners and goalscorers",
  ];

  const fixtures = [
    { home: "England", homeFlag: "🏴󠁧󠁢󠁥󠁮󠁧󠁿", away: "Croatia", awayFlag: "🇭🇷", group: "L", time: "21:00 BST" },
    { home: "France", homeFlag: "🇫🇷", away: "Senegal", awayFlag: "🇸🇳", group: "I", time: "20:00 BST" },
    { home: "Brazil", homeFlag: "🇧🇷", away: "Morocco", awayFlag: "🇲🇦", group: "C", time: "23:00 BST" },
  ];

  return (
    <div style={{ backgroundColor: "#0A0B0F", color: "#FFFFFF" }}>

      {/* Navbar */}
      <nav
        className="sticky top-0 z-50 transition-all"
        style={{
          backgroundColor: scrolled ? "#0A0B0F" : "transparent",
          borderBottom: scrolled ? "1px solid #1E2028" : "none",
          padding: "1.25rem 0",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 flex justify-between items-center">
          <span className="font-display text-3xl tracking-wider" style={{ color: "#C8FF00" }}>
            PREDICTORZ.
          </span>
          <div className="flex items-center gap-4">
            <Link
              to="/login"
              className="font-condensed text-lg tracking-wide transition-colors"
              style={{ color: "#888" }}
            >
              Log in
            </Link>
            <Link
              to="/signup"
              className="font-condensed text-lg tracking-wide px-6 py-2 rounded-xl"
              style={{ backgroundColor: "#C8FF00", color: "#0A0B0F" }}
            >
              SIGN UP FREE
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-32 text-center">
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8"
          style={{ backgroundColor: "#1A1B20", border: "1px solid #2A2B30" }}
        >
          <span className="text-lg">⚽</span>
          <span
            className="font-condensed text-sm tracking-widest uppercase"
            style={{ color: "#C8FF00" }}
          >
            FIFA World Cup 2026 — Starting June 11
          </span>
        </div>

        <h1
          className="font-display tracking-wider leading-none mb-6"
          style={{ fontSize: "clamp(4rem, 12vw, 9rem)", color: "#FFFFFF" }}
        >
          PREDICT.<br />
          <span style={{ color: "#C8FF00" }}>COMPETE.</span><br />
          WIN.
        </h1>

        <p
          className="font-condensed text-xl tracking-wide max-w-2xl mx-auto mb-10"
          style={{ color: "#888", lineHeight: "1.7" }}
        >
          The UK's free football prediction game for the 2026 World Cup.
          Pick scores, predict goalscorers, and challenge your mates in private leagues.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
          <Link
            to="/signup"
            className="font-condensed text-2xl tracking-wide px-10 py-5 rounded-xl"
            style={{ backgroundColor: "#C8FF00", color: "#0A0B0F" }}
          >
            PLAY FOR FREE
          </Link>
          <a
            href="#how-it-works"
            className="font-condensed text-xl tracking-wide px-10 py-5 rounded-xl"
            style={{ border: "1px solid #2A2B30", color: "#888" }}
          >
            HOW IT WORKS
          </a>
        </div>

        {/* Fixture preview cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
          {fixtures.map((fixture, i) => (
            <div
              key={i}
              className="rounded-2xl p-5 text-left"
              style={{
                backgroundColor: "#141519",
                border: "1px solid #1E2028",
                borderTop: "3px solid #C8FF00",
              }}
            >
              <div className="flex justify-between items-center mb-4">
                <span
                  className="font-condensed text-xs tracking-widest uppercase"
                  style={{ color: "#555" }}
                >
                  Group {fixture.group}
                </span>
                <span className="font-condensed text-sm" style={{ color: "#C8FF00" }}>
                  {fixture.time}
                </span>
              </div>
              <div className="flex items-center justify-between mb-4">
                <span className="font-display text-xl tracking-wider" style={{ color: "#FFFFFF" }}>
                  {fixture.homeFlag} {fixture.home}
                </span>
                <span className="font-condensed" style={{ color: "#333" }}>VS</span>
                <span className="font-display text-xl tracking-wider" style={{ color: "#FFFFFF" }}>
                  {fixture.away} {fixture.awayFlag}
                </span>
              </div>
              <div
                className="rounded-lg py-2 text-center font-condensed text-sm tracking-wide"
                style={{ backgroundColor: "#1E2028", color: "#555" }}
              >
                Sign up to predict
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Stats bar */}
      <section style={{ backgroundColor: "#C8FF00" }} className="py-10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="font-display text-6xl tracking-wider" style={{ color: "#0A0B0F" }}>
                  {stat.value}
                </div>
                <div
                  className="font-condensed tracking-widest uppercase text-sm mt-1"
                  style={{ color: "#3A6600" }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <p
              className="font-condensed tracking-widest uppercase text-sm mb-3"
              style={{ color: "#C8FF00" }}
            >
              Simple to play
            </p>
            <h2 className="font-display text-6xl tracking-wider" style={{ color: "#FFFFFF" }}>
              HOW IT WORKS
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((step) => (
              <div
                key={step.number}
                className="rounded-2xl p-6"
                style={{ backgroundColor: "#141519", border: "1px solid #1E2028" }}
              >
                <div
                  className="font-display text-5xl mb-4 tracking-wider"
                  style={{ color: "#C8FF00" }}
                >
                  {step.number}
                </div>
                <h3
                  className="font-display text-2xl tracking-wider mb-3"
                  style={{ color: "#FFFFFF" }}
                >
                  {step.title}
                </h3>
                <p
                  className="font-condensed tracking-wide leading-relaxed"
                  style={{ color: "#666" }}
                >
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Points system */}
      <section className="py-24" style={{ backgroundColor: "#0D0E12" }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <p
              className="font-condensed tracking-widest uppercase text-sm mb-3"
              style={{ color: "#FF5C00" }}
            >
              Points system
            </p>
            <h2 className="font-display text-6xl tracking-wider" style={{ color: "#FFFFFF" }}>
              HOW TO SCORE
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {points.map((item) => (
              <div
                key={item.label}
                className="rounded-2xl p-8 text-center"
                style={{ backgroundColor: "#141519", border: "2px solid " + item.colour }}
              >
                <div
                  className="font-display mb-2"
                  style={{ fontSize: "5rem", color: item.colour, lineHeight: "1" }}
                >
                  {item.pts}
                </div>
                <div
                  className="font-condensed text-xs tracking-widest uppercase mb-2"
                  style={{ color: item.colour }}
                >
                  points
                </div>
                <h3
                  className="font-display text-2xl tracking-wider mb-3"
                  style={{ color: "#FFFFFF" }}
                >
                  {item.label.toUpperCase()}
                </h3>
                <p className="font-condensed tracking-wide" style={{ color: "#555" }}>
                  {item.example}
                </p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <p className="font-condensed tracking-wide" style={{ color: "#444" }}>
              Maximum 8 points per match · Exact score replaces winner points
            </p>
          </div>
        </div>
      </section>

      {/* Leagues section */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

            <div>
              <p
                className="font-condensed tracking-widest uppercase text-sm mb-3"
                style={{ color: "#C8FF00" }}
              >
                Private leagues
              </p>
              <h2
                className="font-display text-6xl tracking-wider mb-6"
                style={{ color: "#FFFFFF" }}
              >
                CHALLENGE YOUR MATES.
              </h2>
              <p
                className="font-condensed text-xl tracking-wide mb-8"
                style={{ color: "#666", lineHeight: "1.7" }}
              >
                Create a private league, share your invite code or link, and compete on
                your own leaderboard throughout the entire tournament.
              </p>
              <div className="space-y-4 mb-10">
                {features.map((feature) => (
                  <div key={feature} className="flex items-start gap-3">
                    <span style={{ color: "#C8FF00" }}>✓</span>
                    <span
                      className="font-condensed text-lg tracking-wide"
                      style={{ color: "#888" }}
                    >
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
              <Link
                to="/signup"
                className="font-condensed text-xl tracking-wide px-8 py-4 rounded-xl inline-block"
                style={{ backgroundColor: "#C8FF00", color: "#0A0B0F" }}
              >
                CREATE YOUR LEAGUE
              </Link>
            </div>

            <div
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: "#141519", border: "1px solid #1E2028" }}
            >
              <div
                className="px-6 py-5 flex justify-between items-center"
                style={{ borderBottom: "1px solid #1E2028", borderTop: "3px solid #C8FF00" }}
              >
                <span className="font-display text-2xl tracking-wider" style={{ color: "#FFFFFF" }}>
                  THE LADS 🏆
                </span>
                <span
                  className="font-condensed text-xs tracking-widest uppercase px-3 py-1 rounded-full"
                  style={{ backgroundColor: "#1E2028", color: "#555" }}
                >
                  6 MEMBERS
                </span>
              </div>

              {mockLeaderboard.map((player, i) => (
                <div
                  key={player.name}
                  className="flex items-center justify-between px-6 py-4"
                  style={{
                    borderBottom: "1px solid #1E2028",
                    backgroundColor: i === 0 ? "#16190F" : "transparent",
                    borderLeft: i === 0 ? "3px solid #C8FF00" : "3px solid transparent",
                  }}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-xl w-6 text-center">{player.medal}</span>
                    <div>
                      <div
                        className="font-condensed text-lg tracking-wide"
                        style={{ color: i === 0 ? "#C8FF00" : "#FFFFFF" }}
                      >
                        {player.name}
                      </div>
                      <div className="font-condensed text-xs tracking-wide" style={{ color: "#444" }}>
                        🎯 {player.scores} · ✅ {player.winners} · ⚽ {player.scorers}
                      </div>
                    </div>
                  </div>
                  <div
                    className="font-display text-3xl tracking-wider"
                    style={{ color: i === 0 ? "#C8FF00" : "#FFFFFF" }}
                  >
                    {player.pts}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 text-center" style={{ backgroundColor: "#0D0E12" }}>
        <div className="max-w-3xl mx-auto px-6">
          <h2
            className="font-display tracking-wider leading-none mb-6"
            style={{ fontSize: "clamp(3rem, 8vw, 6rem)", color: "#FFFFFF" }}
          >
            THE WORLD CUP STARTS
            <br />
            <span style={{ color: "#C8FF00" }}>JUNE 11.</span>
          </h2>
          <p
            className="font-condensed text-xl tracking-wide mb-10"
            style={{ color: "#666" }}
          >
            Sign up free today and make your predictions before kick-off.
            No payment required — ever.
          </p>
          <Link
            to="/signup"
            className="font-condensed text-2xl tracking-wide px-12 py-6 rounded-xl inline-block"
            style={{ backgroundColor: "#C8FF00", color: "#0A0B0F" }}
          >
            SIGN UP FREE — IT TAKES 30 SECONDS
          </Link>
          <p
            className="font-condensed text-sm mt-6 tracking-wide"
            style={{ color: "#333" }}
          >
            No credit card · No subscription · Just football
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: "1px solid #1E2028" }} className="py-10">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <span className="font-display text-2xl tracking-wider" style={{ color: "#C8FF00" }}>
            PREDICTORZ.
          </span>
          <div className="flex items-center gap-6">
            <Link
              to="/login"
              className="font-condensed tracking-wide transition-colors"
              style={{ color: "#555" }}
            >
              Log in
            </Link>
            <Link
              to="/signup"
              className="font-condensed tracking-wide transition-colors"
              style={{ color: "#555" }}
            >
              Sign up
            </Link>
          </div>
          <p
            className="font-condensed text-xs tracking-wide text-center"
            style={{ color: "#333" }}
          >
            18+ · Please gamble responsibly · BeGambleAware.org
          </p>
        </div>
      </footer>

    </div>
  );
}
